import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function UpdateOfficeBearer () {
    const [bearers, setBearers] = useState([]);
    const [editingBearer, setEditingBearer] = useState(null);
    const [formData, setFormData] = useState({ name: '', designation: '' });
    const [profileImage, setProfileImage] = useState(null);
    const [profilePreview, setProfilePreview] = useState(null);

    const navigate = useNavigate();

    const designations = [
        'President',
        'Vice President',
        'General Secretary',
        'Joint Secretary',
        'Treasurer',
        'Member',
    ];

    useEffect(() => {
        fetchBearers();
    }, []);

    const fetchBearers = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/officebearers`);
            setBearers(response.data);
        } catch (error) {
            console.error(error);
            alert('Failed to fetch office bearers');
        }
    };

    const handleEditClick = (bearer) => {
        setEditingBearer(bearer._id);
        setFormData({ name: bearer.name, designation: bearer.designation });
        setProfilePreview(bearer.image);
        setProfileImage(null);
    };

    const handleCancelEdit = () => {
        setEditingBearer(null);
        setFormData({ name: '', designation: '' });
        setProfileImage(null);
        setProfilePreview(null);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleProfileChange = (e) => {
        const file = e.target.files[0];
        setProfileImage(file);
        setProfilePreview(URL.createObjectURL(file));
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        let base64Image = profilePreview; // Default to old preview

        if (profileImage) {
            const reader = new FileReader();
            reader.readAsDataURL(profileImage);

            base64Image = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
            });
        }

        const payload = {
            name: formData.name,
            designation: formData.designation,
            image: base64Image,
        };

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_REQUESTING_API}/api/officebearers/${editingBearer}`,
                payload,
                { headers: { 'Content-Type': 'application/json' } }
            );

            alert(response.data.message);
            fetchBearers(); // Refresh the list
            handleCancelEdit();
        } catch (error) {
            console.error(error);
            alert('Failed to update office bearer');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this Office Bearer?')) {
            try {
                const response = await axios.delete(`${process.env.REACT_APP_REQUESTING_API}/api/officebearers/${id}`);
                alert(response.data.message);
                fetchBearers();
            } catch (error) {
                console.error(error);
                alert('Failed to delete office bearer');
            }
        }
    };

    return (
        <div className="p-6 mt-12 max-w-5xl mx-auto">
            <h1 className="text-3xl font-bold mb-6 text-center">Manage Office Bearers</h1>

            {bearers.length === 0 ? (
                <p className="text-center">No office bearers found.</p>
            ) : (
                <div className="grid gap-6">
                    {bearers.map((bearer) => (
                        <div key={bearer._id} className="border p-4 rounded shadow bg-white">
                            {editingBearer === bearer._id ? (
                                <form onSubmit={handleUpdate} className="space-y-4">
                                    <div>
                                        <label className="block font-semibold">Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="block w-full p-2 border rounded mt-1"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-semibold">Designation</label>
                                        <select
                                            name="designation"
                                            value={formData.designation}
                                            onChange={handleChange}
                                            className="block w-full p-2 border rounded mt-1"
                                            required
                                        >
                                            <option value="">Select Designation</option>
                                            {designations.map((designation, index) => (
                                                <option key={index} value={designation}>
                                                    {designation}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block font-semibold">Upload New Profile Image (optional)</label>
                                        <input
                                            type="file"
                                            name="profileImage"
                                            onChange={handleProfileChange}
                                            accept="image/*"
                                            className="block w-full mt-1"
                                        />
                                        {profilePreview && (
                                            <img
                                                src={profilePreview}
                                                alt="Profile Preview"
                                                className="w-24 h-24 mt-2 object-cover border rounded"
                                            />
                                        )}
                                    </div>

                                    <div className="flex gap-4">
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500"
                                        >
                                            Update
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-300"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <img
                                            src={bearer.image}
                                            alt={bearer.name}
                                            className="w-16 h-16 object-cover border rounded"
                                        />
                                        <div>
                                            <h2 className="text-xl font-semibold">{bearer.name}</h2>
                                            <p className="text-gray-600">{bearer.designation}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEditClick(bearer)}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(bearer._id)}
                                            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-400"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
export default UpdateOfficeBearer 